import React, { useEffect, useMemo, useRef, useState } from 'react';
import OrderDesign from '../OrderDesign';
import { REPOSITION_MOVE_DURATION_MS } from './repositionAnimationTiming';

function findSlotRect(playerId, userId, zone, index) {
  const normalizedPlayerId = String(playerId);
  const normalizedUserId = String(userId);
  const isThisPlayerUnit = normalizedPlayerId === normalizedUserId;
  const slotZoneName = isThisPlayerUnit ? `your_${zone}` : `opponent_${zone}`;
  const slotSelector = `[data-slot-zone="${slotZoneName}"][data-slot-index="${index}"]`;
  const slotElement = document.querySelector(slotSelector);

  if (slotElement) {
    const rect = slotElement.getBoundingClientRect();
    return {
      left: rect.left + window.scrollX,
      top: rect.top + window.scrollY,
      width: rect.width || 180,
      height: rect.height || 120,
    };
  }

  const zoneSelector = isThisPlayerUnit
    ? `.battle-zone.${zone}`
    : `.battle-zone.opponent-${zone}`;
  const zoneContainer = document.querySelector(zoneSelector);
  if (zoneContainer) {
    const slots = Array.from(zoneContainer.querySelectorAll('[data-slot-zone][data-slot-index]'));
    const targetSlot = slots.find((slot) => {
      const slotIndex = parseInt(slot.getAttribute('data-slot-index'), 10);
      const slotZone = slot.getAttribute('data-slot-zone');
      return slotIndex === index && slotZone === slotZoneName;
    });
    if (targetSlot) {
      const rect = targetSlot.getBoundingClientRect();
      return {
        left: rect.left + window.scrollX,
        top: rect.top + window.scrollY,
        width: rect.width || 180,
        height: rect.height || 120,
      };
    }
  }

  return null;
}

function isValidRect(rect) {
  return (
    rect &&
    Number.isFinite(rect.left) &&
    Number.isFinite(rect.top) &&
    Number.isFinite(rect.width) &&
    Number.isFinite(rect.height) &&
    rect.width > 0 &&
    rect.height > 0
  );
}

function buildLayout(reposition, userId) {
  const start = findSlotRect(reposition.playerId, userId, reposition.fromZone, reposition.fromIndex);
  const end = findSlotRect(reposition.playerId, userId, reposition.toZone, reposition.toIndex);
  if (!isValidRect(start) || !isValidRect(end)) {
    return null;
  }

  const startCenterX = start.left + start.width / 2;
  const startCenterY = start.top + start.height / 2;
  const endCenterX = end.left + end.width / 2;
  const endCenterY = end.top + end.height / 2;
  const startLeft = startCenterX - start.width / 2;
  const startTop = startCenterY - start.height / 2;

  return {
    startLeft,
    startTop,
    width: start.width,
    height: start.height,
    dx: endCenterX - startCenterX,
    dy: endCenterY - startCenterY,
  };
}

/**
 * RepositioningOverlay — one continuous fly from source slot to destination slot.
 * Uses a single CSS transform transition (no per-frame React state) and measures DOM once.
 */
export const RepositioningOverlay = ({ reposition, unit, gameState, userId }) => {
  const [layout, setLayout] = useState(null);
  const [atEnd, setAtEnd] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const measureGenerationRef = useRef(0);

  const repositionKey = useMemo(() => {
    if (!reposition) return '';
    return [
      reposition.playerId,
      reposition.instanceId,
      reposition.fromZone,
      reposition.fromIndex,
      reposition.toZone,
      reposition.toIndex,
    ].join('|');
  }, [reposition]);

  const normalizedUserId = String(userId);
  const normalizedRepositionPlayerId = reposition ? String(reposition.playerId) : '';
  const isPlayerUnit = normalizedRepositionPlayerId === normalizedUserId;

  useEffect(() => {
    if (!reposition || !unit || !userId || !repositionKey) return;

    const generation = measureGenerationRef.current + 1;
    measureGenerationRef.current = generation;
    setLayout(null);
    setAtEnd(false);
    setAnimationComplete(false);

    let raf2 = null;
    let retryTimer = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (measureGenerationRef.current !== generation) return;
        const measured = buildLayout(reposition, userId);
        if (measured) {
          setLayout(measured);
          return;
        }
        // One delayed retry if slots were not painted yet.
        retryTimer = setTimeout(() => {
          if (measureGenerationRef.current !== generation) return;
          const retryMeasured = buildLayout(reposition, userId);
          if (retryMeasured) {
            setLayout(retryMeasured);
          }
        }, 100);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [repositionKey, reposition, userId]);

  useEffect(() => {
    if (!layout || animationComplete) return;

    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setAtEnd(true);
      });
    });

    const fallbackTimer = setTimeout(() => {
      setAnimationComplete(true);
    }, REPOSITION_MOVE_DURATION_MS + 80);

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      clearTimeout(fallbackTimer);
    };
  }, [layout, animationComplete]);

  if (!reposition || !unit || !layout || animationComplete) {
    return null;
  }

  const zonePrefix = isPlayerUnit ? 'your' : 'opponent';
  const zoneName = `${zonePrefix}_${reposition.toZone}`;

  return (
    <div
      style={{
        position: 'fixed',
        left: `${layout.startLeft}px`,
        top: `${layout.startTop}px`,
        width: `${layout.width}px`,
        height: `${layout.height}px`,
        zIndex: 100000,
        pointerEvents: 'none',
        transform: atEnd ? `translate(${layout.dx}px, ${layout.dy}px)` : 'translate(0, 0)',
        transition: atEnd
          ? `transform ${REPOSITION_MOVE_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`
          : 'none',
        willChange: 'transform',
        isolation: 'isolate',
      }}
      onTransitionEnd={(event) => {
        if (event.propertyName !== 'transform') return;
        setAnimationComplete(true);
      }}
    >
      <OrderDesign
        order={unit}
        minimal={true}
        zoneName={zoneName}
        slotIndex={reposition.toIndex}
        gameState={gameState}
        userId={userId}
      />
    </div>
  );
};
